import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryKeys } from "@/lib/queries/query-keys";

interface AuthApiResponse {
  status: string;
  timestamp: string;
}

/**
 * Custom hook to test the authenticated API endpoint
 * Uses React Query mutation for better state management
 */
export function useTestAuthApi() {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMutation({
    mutationKey: queryKeys.auth.testApi,
    mutationFn: async () => {
      const response = await authenticatedFetch("/api/authenticated");

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication required");
        }
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      return response.json() as Promise<AuthApiResponse>;
    },
    onSuccess: () => {
      toast.success("API call successful!");
    },
    onError: (error: Error) => {
      if (error.message === "Authentication required") {
        toast.error("You need to be logged in to test the authenticated API");
      } else {
        toast.error(`API call failed: ${error.message}`);
      }
    },
  });
}
